import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../hooks/usePageMeta';
import { 
  ArrowRight, 
  Search, 
  TrendingUp, 
  Globe, 
  Share2, 
  Target, 
  Palette,
  CheckCircle,
  XCircle,
  Star,
  Users,
  Award,
  Zap,
  ChevronRight,
  Building2,
  GraduationCap,
  Heart,
  Store,
  Rocket,
  Briefcase,
  Plus,
  Minus,
  BarChart3,
  MousePointer,
  FileSearch,
  Lightbulb,
  LineChart,
  MessageSquare
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

// Services data with SEO keywords
const services = [
  {
    icon: Search,
    title: 'SEO & Organic Growth',
    description: 'Rank higher, attract buyers & reduce ad dependency with data-driven SEO.',
    slug: 'seo-organic-growth',
    keyword: 'SEO Services'
  },
  {
    icon: TrendingUp,
    title: 'Performance Marketing',
    description: 'ROI-focused Google & Meta ads that generate qualified leads, not just clicks.',
    slug: 'performance-marketing',
    keyword: 'PPC Management'
  },
  {
    icon: Globe,
    title: 'Website Design & Development',
    description: 'Conversion-optimised websites that turn visitors into customers.',
    slug: 'website-design-development',
    keyword: 'Web Development'
  },
  {
    icon: Share2,
    title: 'Social Media Marketing',
    description: 'Build engaged communities and brand awareness that drives sales.',
    slug: 'social-media-marketing',
    keyword: 'Social Media'
  },
  {
    icon: Target,
    title: 'Conversion Rate Optimization',
    description: 'Turn more visitors into paying customers with CRO strategies.',
    slug: 'conversion-rate-optimization',
    keyword: 'CRO Services'
  },
  {
    icon: Palette,
    title: 'Branding & Strategy',
    description: 'Create memorable brands that command premium pricing.',
    slug: 'branding-strategy',
    keyword: 'Brand Strategy'
  },
];

// Industries served
const industries = [
  { icon: Heart, title: 'Healthcare & Aesthetics', slug: 'healthcare-aesthetics', desc: 'Patient acquisition strategies' },
  { icon: GraduationCap, title: 'Education & Coaching', slug: 'education-coaching', desc: 'Enrollment-focused marketing' },
  { icon: Building2, title: 'Real Estate', slug: 'real-estate', desc: 'Lead generation for property' },
  { icon: Briefcase, title: 'Professional Services', slug: 'professional-services', desc: 'B2B lead generation' },
  { icon: Store, title: 'Local Businesses', slug: 'local-businesses', desc: 'Local SEO & foot traffic' },
  { icon: Rocket, title: 'Startups & SMEs', slug: 'startups-smes', desc: 'Growth-focused marketing' },
];

// Stats/Trust data
const stats = [
  { number: '6+', label: 'Years Experience', icon: Award },
  { number: '150+', label: 'Happy Clients', icon: Users },
  { number: '4000%', label: 'Avg. ROI Growth', icon: TrendingUp },
  { number: '15+', label: 'Expert Team', icon: Zap },
];

// Problems and Solutions
const problems = [
  { text: 'Low quality leads that don\'t convert', icon: XCircle },
  { text: 'High advertising costs with poor ROI', icon: XCircle },
  { text: 'Website traffic but no conversions', icon: XCircle },
  { text: 'Invisible on Google search results', icon: XCircle },
];

const solutions = [
  { text: 'Data-driven SEO for qualified traffic', icon: CheckCircle },
  { text: 'ROI-focused ad campaigns', icon: CheckCircle },
  { text: 'CRO-optimised websites', icon: CheckCircle },
  { text: 'First page Google rankings', icon: CheckCircle },
];

// Process steps
const processSteps = [
  {
    number: '01',
    title: 'Audit',
    description: 'We analyse your business, website, competitors and market to identify growth opportunities.',
    icon: FileSearch
  },
  {
    number: '02',
    title: 'Strategy',
    description: 'We create a customized digital marketing plan aligned with your business goals and budget.',
    icon: Lightbulb
  },
  {
    number: '03',
    title: 'Execution',
    description: 'We implement, test and optimise campaigns for maximum performance and ROI.',
    icon: MousePointer
  },
  {
    number: '04',
    title: 'Scale',
    description: 'We scale winning strategies and continuously improve results month over month.',
    icon: LineChart
  },
];

// Testimonials with results
const testimonials = [
  {
    name: 'Dr. Geetanjali Shetty',
    role: 'Founder',
    company: 'Aesthetic Clinic',
    content: 'Digital Startup helped us increase patient inquiries by 300%. Their SEO expertise is unmatched.',
    image: 'https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=200',
    rating: 5,
    metric: '+300%',
    metricLabel: 'Patient Inquiries'
  },
  {
    name: 'Rahul Mehta',
    role: 'Managing Director',
    company: 'Pali Village Resort',
    content: 'Our footfall increased by 40% in just 3 months. Best marketing investment we\'ve made.',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200',
    rating: 5,
    metric: '+40%',
    metricLabel: 'Footfall Growth'
  },
  {
    name: 'Priya Sharma',
    role: 'Principal',
    company: 'HVB Global Academy',
    content: 'Admission inquiries tripled. Their strategic approach to digital marketing is exactly what educational institutions need.',
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200',
    rating: 5,
    metric: '3x',
    metricLabel: 'Admission Inquiries'
  },
];

// FAQs for SEO
const faqs = [
  {
    question: 'How long does SEO take to show results?',
    answer: 'SEO typically takes 3-6 months to show significant results, depending on your industry competition and current website state. However, we often see initial improvements within the first 4-8 weeks. Our approach focuses on sustainable, long-term growth rather than quick fixes.'
  },
  {
    question: 'Do you guarantee leads or rankings?',
    answer: 'While we cannot guarantee specific rankings (no ethical agency can), we guarantee our commitment to delivering measurable improvements. Our track record shows an average ROI growth of 4000% for our clients. We provide transparent reporting so you can track progress.'
  },
  {
    question: 'What industries do you specialise in?',
    answer: 'We have deep expertise in Healthcare, Education, Real Estate, Professional Services, Local Businesses, and Startups/SMEs. Our industry-specific knowledge allows us to create targeted strategies that resonate with your audience and comply with industry regulations.'
  },
  {
    question: 'How much does digital marketing cost?',
    answer: 'Our pricing is customized based on your goals, competition, and budget. We offer flexible packages starting from basic SEO to comprehensive digital marketing solutions. Contact us for a free audit and transparent pricing tailored to your needs.'
  },
  {
    question: 'What makes Digital Startup different from other agencies?',
    answer: 'We focus on revenue, not vanity metrics. With 6+ years of experience and 150+ clients, we deliver data-driven strategies with transparent reporting. We work as your growth partner, not just a service provider.'
  },
];

// Scroll animation hook
const useScrollAnimation = () => {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = ref.current?.querySelectorAll('.scroll-animate');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return ref;
};

export const Home = ({ onOpenAuditModal }) => {
  const scrollRef = useScrollAnimation();
  usePageMeta();

  return (
    <main ref={scrollRef} data-testid="home-page">
      {/* SECTION 1: HERO - Above the Fold */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 bg-[#F9FAFB] overflow-hidden" data-testid="hero-section">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#FFAA17]/10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#2F80ED]/5 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm font-semibold tracking-wide uppercase text-[#FFAA17] mb-4 animate-fade-up">
              Digital Marketing Agency
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#1A1A1A] mb-6 animate-fade-up animation-delay-100">
              Digital Marketing That Turns{' '}
              <span className="text-[#FFAA17]">Traffic Into Revenue</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#525252] mb-8 max-w-2xl mx-auto animate-fade-up animation-delay-200">
              We help businesses generate qualified leads using SEO, ads & conversion-focused websites. 
              Over 6+ years of delivering measurable ROI.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up animation-delay-300">
              <Button
                onClick={onOpenAuditModal}
                className="bg-[#FFAA17] hover:bg-[#E69500] text-[#1A1A1A] font-semibold rounded-full px-8 py-4 text-lg shadow-lg shadow-amber-500/25 btn-hover-lift"
                data-testid="hero-cta-primary"
              >
                Get Free Growth Audit
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-[#2F80ED] text-[#2F80ED] hover:bg-[#2F80ED]/5 rounded-full px-8 py-4 text-lg transition-colors"
                data-testid="hero-cta-secondary"
              >
                <Link to="/contact">Talk to an Expert</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: TRUSTED BY - Logo Carousel */}
      <section className="py-10 bg-white border-b border-gray-100" data-testid="trusted-by-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <p className="text-center text-sm text-gray-500 mb-8 font-medium">TRUSTED BY 150+ BRANDS ACROSS INDIA & UAE</p>
          <div className="logo-carousel">
            <div className="logo-track">
              {/* First set of logos */}
              <div className="flex items-center justify-center min-w-[180px] h-16 px-6 hover:opacity-100 transition-all opacity-80">
                <img src="./images/client-orthofit.png" alt="Orthofit Mart" className="h-12 w-auto object-contain" />
              </div>
              <div className="flex items-center justify-center min-w-[180px] h-16 px-6 hover:opacity-100 transition-all opacity-80">
                <img src="./images/client-medscape.png" alt="Medscape India" className="h-12 w-auto object-contain" />
              </div>
              <div className="flex items-center justify-center min-w-[180px] h-16 px-6 hover:opacity-100 transition-all opacity-80 bg-gray-800 rounded-lg">
                <img src="./images/client-studioarch.png" alt="Studio Arch" className="h-10 w-auto object-contain" />
              </div>
              <div className="flex items-center justify-center min-w-[180px] h-16 px-6 hover:opacity-100 transition-all opacity-80">
                <img src="./images/client-mediaskills.png" alt="MediaSkills Academy" className="h-12 w-auto object-contain" />
              </div>
              <div className="flex items-center justify-center min-w-[180px] h-16 px-6 hover:opacity-100 transition-all opacity-80">
                <img src="./images/client-aryan.png" alt="Aryan Hospital" className="h-12 w-auto object-contain" />
              </div>
              {/* Duplicate set for seamless loop */}
              <div className="flex items-center justify-center min-w-[180px] h-16 px-6 hover:opacity-100 transition-all opacity-80">
                <img src="./images/client-orthofit.png" alt="Orthofit Mart" className="h-12 w-auto object-contain" />
              </div>
              <div className="flex items-center justify-center min-w-[180px] h-16 px-6 hover:opacity-100 transition-all opacity-80">
                <img src="./images/client-medscape.png" alt="Medscape India" className="h-12 w-auto object-contain" />
              </div>
              <div className="flex items-center justify-center min-w-[180px] h-16 px-6 hover:opacity-100 transition-all opacity-80 bg-gray-800 rounded-lg">
                <img src="./images/client-studioarch.png" alt="Studio Arch" className="h-10 w-auto object-contain" />
              </div>
              <div className="flex items-center justify-center min-w-[180px] h-16 px-6 hover:opacity-100 transition-all opacity-80">
                <img src="./images/client-mediaskills.png" alt="MediaSkills Academy" className="h-12 w-auto object-contain" />
              </div>
              <div className="flex items-center justify-center min-w-[180px] h-16 px-6 hover:opacity-100 transition-all opacity-80">
                <img src="./images/client-aryan.png" alt="Aryan Hospital" className="h-12 w-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: TRUST & SOCIAL PROOF */}
      <section className="py-12 bg-[#2E2E2E]" data-testid="trust-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center scroll-animate" style={{ animationDelay: `${index * 100}ms` }}>
                  <Icon className="w-8 h-8 text-[#FFAA17] mx-auto mb-2" />
                  <p className="text-3xl md:text-4xl font-bold text-white">{stat.number}</p>
                  <p className="text-sm text-gray-400 mt-1">{stat.label}</p>
                </div>
              );
            })}
          </div>
          
          {/* Client trust indicators */}
          <div className="mt-10 pt-8 border-t border-white/10">
            <p className="text-center text-sm text-gray-400 mb-6">Trusted by 150+ businesses across India & UAE</p>
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
              {['SMEs', 'Healthcare', 'Education', 'Real Estate', 'E-commerce'].map((industry, i) => (
                <span key={i} className="text-gray-500 text-sm font-medium px-4 py-2 bg-white/5 rounded-full">
                  {industry}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: PROBLEM → SOLUTION → OUTCOME */}
      <section className="py-16 md:py-24 bg-white" data-testid="problem-solution-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-animate">
            <p className="text-sm font-semibold tracking-wide uppercase text-[#FFAA17] mb-3">
              Why Businesses Choose Us
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A1A1A]">
              From Marketing Problems to Growth Solutions
            </h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Problems */}
            <div className="scroll-animate">
              <h3 className="text-xl font-semibold text-[#1A1A1A] mb-6 flex items-center gap-2">
                <span className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-500" />
                </span>
                Common Marketing Problems
              </h3>
              <div className="space-y-4">
                {problems.map((problem, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-4 p-4 bg-red-50/50 rounded-xl border border-red-100/50"
                  >
                    <XCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
                    <span className="text-[#525252]">{problem.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Solutions */}
            <div className="scroll-animate animation-delay-200">
              <h3 className="text-xl font-semibold text-[#1A1A1A] mb-6 flex items-center gap-2">
                <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#27AE60]" />
                </span>
                Our Solutions
              </h3>
              <div className="space-y-4">
                {solutions.map((solution, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-4 p-4 bg-green-50/50 rounded-xl border border-green-100/50"
                  >
                    <CheckCircle className="w-6 h-6 text-[#27AE60] flex-shrink-0" />
                    <span className="text-[#525252]">{solution.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: SERVICES (SEO RICH) */}
      <section className="py-16 md:py-24 bg-[#F2F2F2]" data-testid="services-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-animate">
            <p className="text-sm font-semibold tracking-wide uppercase text-[#FFAA17] mb-3">
              What We Do
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A1A1A]">
              Services That Drive Revenue
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Link
                  key={index}
                  to={`/services/${service.slug}`}
                  className="group bg-white rounded-2xl p-6 border border-transparent hover:border-[#FFAA17]/20 hover:shadow-[0_10px_40px_rgba(255,170,23,0.12)] transition-all duration-300 scroll-animate"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`service-card-${service.slug}`}
                >
                  <div className="w-14 h-14 bg-[#FFAA17]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FFAA17] transition-colors duration-300">
                    <Icon className="w-7 h-7 text-[#FFAA17] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <span className="text-xs font-medium text-[#2F80ED] uppercase tracking-wide">{service.keyword}</span>
                  <h3 className="text-xl font-semibold text-[#1A1A1A] mt-1 mb-2">{service.title}</h3>
                  <p className="text-[#525252] text-sm mb-4">{service.description}</p>
                  <span className="inline-flex items-center text-[#FFAA17] font-medium text-sm group-hover:gap-2 transition-all">
                    View {service.keyword} <ChevronRight className="w-4 h-4 ml-1" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 6: INDUSTRIES SERVED */}
      <section className="py-16 md:py-24 bg-white" data-testid="industries-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-animate">
            <p className="text-sm font-semibold tracking-wide uppercase text-[#FFAA17] mb-3">
              Industries We Serve
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A1A1A]">
              Expertise Across Sectors
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <Link
                  key={index}
                  to={`/industries/${industry.slug}`}
                  className="group relative bg-[#F9FAFB] rounded-2xl p-6 text-center hover:bg-[#FFAA17] transition-all duration-300 overflow-hidden scroll-animate"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`industry-card-${industry.slug}`}
                >
                  <Icon className="w-10 h-10 mx-auto mb-3 text-[#2F80ED] group-hover:text-white transition-colors" />
                  <h3 className="font-semibold text-[#1A1A1A] group-hover:text-white text-sm transition-colors">
                    {industry.title}
                  </h3>
                  <p className="text-xs text-[#525252] group-hover:text-white/80 mt-1 transition-colors">
                    {industry.desc}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 7: RESULTS & TESTIMONIALS */}
      <section className="py-16 md:py-24 bg-[#F2F2F2]" data-testid="testimonials-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-animate">
            <p className="text-sm font-semibold tracking-wide uppercase text-[#FFAA17] mb-3">
              Client Results
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A1A1A]">
              Real Growth, Real Businesses
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 scroll-animate"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`testimonial-card-${index}`}
              >
                {/* Result metric */}
                <div className="bg-[#27AE60]/10 rounded-xl px-4 py-3 mb-6 inline-block">
                  <span className="text-2xl font-bold text-[#27AE60]">{testimonial.metric}</span>
                  <span className="text-xs text-[#27AE60] ml-2">{testimonial.metricLabel}</span>
                </div>
                
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-[#F2C94C] fill-[#F2C94C]" />
                  ))}
                </div>
                <p className="text-[#525252] mb-6 italic">"{testimonial.content}"</p>
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">{testimonial.name}</p>
                    <p className="text-sm text-[#525252]">{testimonial.role}, {testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <Button
              asChild
              variant="outline"
              className="border-[#2F80ED] text-[#2F80ED] hover:bg-[#2F80ED]/5 rounded-full px-6"
            >
              <Link to="/work">View All Case Studies <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 8: PROCESS (CONVERSION PSYCHOLOGY) */}
      <section className="py-16 md:py-24 bg-white" data-testid="process-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-animate">
            <p className="text-sm font-semibold tracking-wide uppercase text-[#FFAA17] mb-3">
              Our Process
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A1A1A]">
              How We Deliver Results
            </h2>
            <p className="text-[#525252] mt-4 max-w-2xl mx-auto">
              A proven 4-step framework that reduces risk and maximizes ROI
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div 
                  key={index} 
                  className="relative scroll-animate"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`process-step-${index + 1}`}
                >
                  {/* Connector line */}
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-12 left-[calc(50%+40px)] w-[calc(100%-80px)] h-0.5 bg-[#E5E5E5]" />
                  )}
                  
                  <div className="bg-[#F9FAFB] rounded-2xl p-6 text-center relative">
                    <div className="w-16 h-16 bg-[#FFAA17] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <span className="text-4xl font-bold text-[#FFAA17]/20 absolute top-4 left-4">
                      {step.number}
                    </span>
                    <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">{step.title}</h3>
                    <p className="text-[#525252] text-sm">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 9: FAQ (SEO GOLD) */}
      <section className="py-16 md:py-24 bg-[#F9FAFB]" data-testid="faq-section">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-12 scroll-animate">
            <p className="text-sm font-semibold tracking-wide uppercase text-[#FFAA17] mb-3">
              FAQs
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]">
              Frequently Asked Questions
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="scroll-animate">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white rounded-xl mb-3 border border-[#E5E5E5] px-6 data-[state=open]:border-[#FFAA17]/30"
              >
                <AccordionTrigger className="text-left font-semibold text-[#1A1A1A] hover:text-[#FFAA17] py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#525252] pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* SECTION 10: FINAL CTA (MOST IMPORTANT) */}
      <section className="py-16 md:py-24 bg-[#2E2E2E]" data-testid="final-cta-section">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <div className="scroll-animate">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
              Ready to Scale Your Business Online?
            </h2>
            <p className="text-gray-400 mb-8 text-lg">
              Get a free growth audit & actionable insights. No commitment required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={onOpenAuditModal}
                className="bg-[#FFAA17] hover:bg-[#E69500] text-[#1A1A1A] font-semibold rounded-full px-8 py-4 text-lg shadow-lg shadow-amber-500/25 btn-hover-lift"
                data-testid="final-cta-primary"
              >
                Get Free Growth Audit
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 rounded-full px-8 py-4 text-lg"
                data-testid="final-cta-secondary"
              >
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Schema for SEO - JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })
        }}
      />
      
      {/* Organization Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Digital Startup",
            "url": "https://digitalstartup.in",
            "logo": "https://digitalstartup.in/logo.png",
            "description": "Digital marketing agency specializing in SEO, performance marketing, and conversion optimization",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Mumbai",
              "addressCountry": "IN"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-9867958759",
              "contactType": "sales",
              "email": "hello@digitalstartup.in"
            }
          })
        }}
      />
    </main>
  );
};

export default Home;
